import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";

function SignaturePad({ value, onChange, disabled = false }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [lastClearedValue, setLastClearedValue] = useState("");

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const touch = event.touches?.[0];
    const clientX = touch ? touch.clientX : event.clientX;
    const clientY = touch ? touch.clientY : event.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (event) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const point = getPoint(event);
    if (!canvas || !point) return;
    const ctx = canvas.getContext("2d");
    drawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const continueDrawing = (event) => {
    if (disabled) return;
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const point = getPoint(event);
    if (!canvas || !point) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (disabled) return;
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange?.(canvas.toDataURL("image/png"));
  };

  const clearCanvas = () => {
    if (disabled) return;
    if (!value) return;
    const shouldClear = window.confirm(
      "Clear this signature? You can undo right after clearing."
    );
    if (!shouldClear) return;
    setLastClearedValue(value);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange?.("");
  };
  const undoClear = () => {
    if (disabled || !lastClearedValue) return;
    onChange?.(lastClearedValue);
    setLastClearedValue("");
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsReady(true);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isReady) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (!value) return;
    const image = new Image();
    image.onload = () => {
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    };
    image.src = value;
  }, [value, isReady]);

  return (
    <Box>
      <Box
        sx={{
          border: "1px dashed #cbd5e1",
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <canvas
          ref={canvasRef}
          width={460}
          height={120}
          style={{ width: "100%", height: 120, display: "block", touchAction: "none" }}
          onMouseDown={startDrawing}
          onMouseMove={continueDrawing}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={continueDrawing}
          onTouchEnd={stopDrawing}
        />
      </Box>
      <Stack
        direction="row"
        spacing={1}
        className="no-print"
        sx={{
          mt: 1,
          "@media print": {
            display: "none",
          },
        }}
      >
        <Button size="small" variant="outlined" onClick={clearCanvas} disabled={disabled || !value}>
          Clear
        </Button>
        {lastClearedValue ? (
          <Button size="small" variant="text" onClick={undoClear} disabled={disabled}>
            Undo Clear
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

const formatSignedAt = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString();
};

export default function DocumentSignatureSection({
  title = "Signatures",
  description = "",
  roles = [],
  signatures = {},
  onChange,
  showEditor = true,
  showPrintBlock = true,
  printTopMargin = 3,
  disabled = false,
}) {
  const normalizedRoles = useMemo(
    () =>
      roles.map((role) => ({
        key: role.key,
        label: role.label,
        helper: role.helper || "",
      })),
    [roles]
  );

  const updateSignature = (key, imageData) => {
    const next = {
      ...(signatures || {}),
      [key]: imageData
        ? { imageData, signedAt: new Date().toISOString() }
        : { imageData: "", signedAt: "" },
    };
    onChange?.(next);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {showEditor && (
        <Box
          className="no-print"
          sx={{
            p: 2,
            border: "1px solid #e2e8f0",
            borderRadius: 1.5,
            backgroundColor: "#fff",
            "@media print": {
              display: "none",
            },
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, mb: 1.5 }}>
              {description}
            </Typography>
          ) : null}
          <Grid container spacing={2}>
            {normalizedRoles.map((role) => {
              const roleValue = signatures?.[role.key]?.imageData || "";
              const signedAt = formatSignedAt(signatures?.[role.key]?.signedAt);
              return (
                <Grid item xs={12} md={6} key={role.key}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                    {role.label}
                  </Typography>
                  <SignaturePad
                    value={roleValue}
                    onChange={(imageData) => updateSignature(role.key, imageData)}
                    disabled={disabled}
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                    {role.helper || "Sign digitally for this document"}
                    {signedAt ? ` • Signed: ${signedAt}` : ""}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {showPrintBlock && (
        <Box
          sx={{
            display: "none",
            "@media print": {
              display: "block",
              mt: printTopMargin,
              breakInside: "avoid",
            },
          }}
        >
          <Divider sx={{ mb: 1.25 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {title}
          </Typography>
          <Grid container spacing={2}>
            {normalizedRoles.map((role) => {
              const roleData = signatures?.[role.key] || {};
              const hasSignature = Boolean(roleData.imageData);
              const signedAt = formatSignedAt(roleData.signedAt);
              return (
                <Grid item xs={12} md={6} key={`${role.key}-print`}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {role.label}
                  </Typography>
                  {hasSignature ? (
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                      <Box
                        sx={{
                          borderBottom: "1px solid #222",
                          width: 240,
                          height: 68,
                          display: "flex",
                          alignItems: "flex-end",
                        }}
                      >
                        <img
                          src={roleData.imageData}
                          alt={`${role.label} signature`}
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </Box>
                      <Typography variant="caption">Date: {signedAt || "__________"}</Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2">________________________  Date: __________</Typography>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
